/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { VodInteractor } from '../interactors';
import { TActionDetailsList, TVodWatchAction, VodRequiredParams } from '../types';

/**
 * This module defines the `VodPlayActionHelper` class and its associated methods for managing
 * VOD play actions. It imports required kernels, types, and interactors to handle playback,
 * content management, and related VOD functionalities.
 */

export class VodPlayAction {
    public static readonly GLOBAL_MOUSE_TRAP_FORMAT_SELECTOR_OVERLAY =
        'HideVodFormatSelectorOverlay';

    public static readonly GLOBAL_MOUSE_TRAP_VOD_RESUME_OVERLAY = 'HideVodResumeStartOverOverlay';

    // eslint-disable-next-line class-methods-use-this
    public async getVodOttActionDetails(
        vodRequiredParams: VodRequiredParams,
    ): Promise<TActionDetailsList> {
        const { itemType } = vodRequiredParams;
        let actionDetails;
        switch (itemType) {
            case 'offer':
                actionDetails = await new VodInteractor().fetchOttOfferActionDetails({
                    isCatchUp: false,
                    isStartOver: false,
                    screenType: 'VOD',
                    offerId: vodRequiredParams.id,
                });
                break;
            case 'content':
                actionDetails = await new VodInteractor().fetchOttContentActionDetails({
                    contentId: vodRequiredParams.id,
                    screenType: 'VOD',
                    contentSupplierPartnerId: vodRequiredParams.contentSupplierPartnerId,
                    collapsedVodItemId: vodRequiredParams.collapsedVodItemId,
                    videoProviderId: vodRequiredParams.partnerId,
                });
                break;
            case 'unifiedCollection':
                actionDetails = await new VodInteractor().getUnifiedCollectionActionDetails({
                    screenType: 'VOD',
                    unifiedCollectionId: vodRequiredParams.id,
                    contentSupplierPartnerId: vodRequiredParams.contentSupplierPartnerId,
                    collapsedVodItemId: vodRequiredParams.collapsedVodItemId,
                    videoProviderId: vodRequiredParams.partnerId,
                });
                break;
            default:
                break;
        }
        return actionDetails;
    }

    // eslint-disable-next-line class-methods-use-this
    public async purchaseVodItem(
        selectedAction: TVodWatchAction,
        applicationFeatureArea: string = 'VOD',
    ): Promise<any> {
        const { partnerOfferId } = selectedAction.kernel || {};
        const vodInteractor = new VodInteractor();
        return vodInteractor.purchaseVodItem({
            applicationFeatureArea,
            partnerOfferId,
            price: 'USD.0',
        });
    }

    // eslint-disable-next-line class-methods-use-this
    public checkForPausePoint(selectedAction: TVodWatchAction): boolean {
        // Check if there's a pause point/resume position
        const { savedPosition, duration } = selectedAction.kernel || {};
        if (!savedPosition || (duration && savedPosition >= duration)) {
            return false;
        }
        return true;
    }
}
